import { type DocBlock, type DocPage, DocGroup } from './portal-types';

const heading = (text: string): DocBlock => ({ kind: 'heading', text });
const paragraph = (text: string): DocBlock => ({ kind: 'paragraph', text });
const code = (language: string, codeText: string): DocBlock => ({
  kind: 'code',
  language,
  code: codeText,
});
const callout = (text: string): DocBlock => ({ kind: 'callout', text });
const list = (items: readonly string[]): DocBlock => ({ kind: 'list', items });
const table = (headers: readonly string[], rows: readonly (readonly string[])[]): DocBlock => ({
  kind: 'table',
  headers,
  rows,
});

export const DOC_PAGES: readonly DocPage[] = [
  {
    id: 'introduction',
    group: DocGroup.GettingStarted,
    title: 'Introduction',
    lead: 'The CtrlChain API is a REST API for booking and tracking freight on the CtrlChain network. Use it to fetch your booking users and lanes, place lane bookings, follow the resulting orders, and receive webhook notifications when their status changes.',
    blocks: [
      heading('Base URL'),
      code('base url', 'https://api.ctrlchain.com/api/v1'),
      paragraph(
        'The API speaks JSON and returns conventional HTTP status codes. All endpoints live under the same base URL and are authenticated with the same API key.',
      ),
      heading('Versioning'),
      paragraph(
        'The major version lives in the path (/api/v1). Backwards-compatible changes — new fields, new endpoints, new webhook event types — can ship without notice, so your JSON parsing must tolerate unknown fields.',
      ),
      callout(
        'The complete, machine-readable contract is published as an OpenAPI document at api.ctrlchain.com/swagger/v1/swagger.json — use it to generate clients and mock servers.',
      ),
    ],
  },
  {
    id: 'authentication',
    group: DocGroup.GettingStarted,
    title: 'Authentication',
    lead: 'Every request is authenticated with an apiKey header. Keys are issued per enterprise by CtrlChain.',
    blocks: [
      code(
        'curl',
        'curl https://api.ctrlchain.com/api/v1/Lane/get-lanes \\\n  -H "apiKey: <your-api-key>"',
      ),
      paragraph(
        'A key is scoped to your enterprise: it sees your own groups, booking users, lanes, orders, and webhook events. There is no self-serve signup — request a key through your CtrlChain account manager or info@ctrlchain.com.',
      ),
      callout(
        'Keep keys server-side only. Never embed them in mobile apps or browser code — anyone holding the key can book freight on your account.',
      ),
    ],
  },
  {
    id: 'errors',
    group: DocGroup.GettingStarted,
    title: 'Errors',
    lead: 'Errors use conventional HTTP status codes and carry a problem details body (RFC 7807).',
    blocks: [
      code(
        'error response',
        '{\n  "type": "https://tools.ietf.org/html/rfc7231#section-6.5.1",\n  "title": "Bad Request",\n  "status": 400,\n  "detail": "A lane with the provided laneId could not be found."\n}',
      ),
      table(
        ['Status', 'Meaning'],
        [
          ['400', 'Malformed or invalid request — missing fields or unknown ids.'],
          ['401', 'Missing or invalid apiKey header.'],
          ['403', 'The key is valid but not allowed to access this resource.'],
          ['500', 'Something failed on our side. Safe to retry with backoff.'],
        ],
      ),
      paragraph(
        'If an error persists and you believe your request is correct, contact info@ctrlchain.com with the request you sent and the response you received.',
      ),
    ],
  },
  {
    id: 'users',
    group: DocGroup.Resources,
    title: 'Users',
    lead: 'Users are personas in the system, organised in groups. To place a booking you need both a booking user and the group to book for.',
    blocks: [
      heading('Groups'),
      paragraph(
        'Groups are organisational structures. A shipper can have an enterprise group with multiple subgroups that mirror its own structure — for example a European holding with per-country subgroups. Bookings can be placed on a subgroup.',
      ),
      heading('Booking users'),
      paragraph(
        'A booker is a user that has the Booker role inside a given group. The get-booking-users endpoint returns all users within your own enterprise that hold this role, grouped per (sub)group.',
      ),
      callout(
        'To make a booking with the API, you need to provide both the booker userId and the groupId of the (sub)group you want to place the booking for.',
      ),
      code(
        'curl',
        'curl https://api.ctrlchain.com/api/v1/User/get-booking-users \\\n  -H "apiKey: <your-api-key>"',
      ),
    ],
  },
  {
    id: 'lanes',
    group: DocGroup.Resources,
    title: 'Lanes',
    lead: 'Lanes are agreements between CtrlChain and the shipper. They have a validity based on a time range, and the API only returns currently valid lanes.',
    blocks: [
      heading('Lane definition'),
      list([
        'A pickup and delivery location',
        'The type of vehicle',
        'Optionally the type of trailer',
        'Agreed rates spanning a certain time range — for example €500 from January to June, €600 from July to December',
        'Optional default contact persons for the pickup and/or delivery location',
      ]),
      paragraph(
        'Lanes are set up commercially — you cannot create them via the API. List them with get-lanes, cache the ids, and pass the laneId when requesting a booking.',
      ),
      callout(
        'If a lane is missing from the returned list, it is most likely outside its validity period — contact your account manager.',
      ),
    ],
  },
  {
    id: 'requests',
    group: DocGroup.Resources,
    title: 'Requests',
    lead: 'A request is a request to place an order. The platform verifies your input and, when all fields are filled in correctly, an order is generated immediately.',
    blocks: [
      heading('Request definition'),
      list([
        'laneId — fetched from the Lane API',
        'groupId and userId — fetched from the User API',
        'Desired pickup and delivery date times, in the timezone of the address',
        'References and comments for the pickup / delivery location and for the shipper',
        'tailLift — whether the order needs a tail lift',
        'A minimum and maximum temperature, required when the lane uses a refrigerated (Reefer) trailer',
      ]),
      code(
        'curl',
        'curl -X POST https://api.ctrlchain.com/api/v1/Request/lane-booking \\\n  -H "apiKey: <your-api-key>" \\\n  -H "Content-Type: application/json" \\\n  -d \'{\n    "laneId": "5a1c9e7d-2b3f-4a6c-8d9e-0f1a2b3c4d5e",\n    "groupId": "8c2e1b4a-9d3f-4c5e-a6b7-c8d9e0f1a2b3",\n    "userId": "b9b0054f-000f-4e0d-a529-555f31821541",\n    "tailLift": false,\n    "desiredPickupDateTimes": {\n      "earliest": "2026-08-04T06:00:00",\n      "latest": "2026-08-04T10:00:00"\n    }\n  }\'',
      ),
      paragraph(
        'With this version of the API it is only possible to create one booking at a time. For recurring orders, call the same endpoint several times with the applicable dates and times.',
      ),
    ],
  },
  {
    id: 'orders',
    group: DocGroup.Resources,
    title: 'Orders',
    lead: 'An order contains all relevant data related to a shipment. Orders are created by the platform when a lane booking request is accepted.',
    blocks: [
      heading('Order definition'),
      list([
        'The related (sub)group and the booker',
        'Stops with dates, references, and instructions',
        'Desired pickup and delivery date times',
        'Vehicle, trailer, and driver data once a carrier is assigned',
        'Uploaded PODs, with a download link once they are approved',
      ]),
      heading('Order details'),
      paragraph(
        'GET /api/v1/Order/detail/{orderId} returns the full order, including its status, stops, and carrier details. Webhook notifications include a DetailUrl that points straight at this endpoint for the affected order.',
      ),
      heading('Documents'),
      paragraph(
        'Once the PODs related to an order are approved, they appear in the approvedPODs array with a downloadUrl. Files can also be fetched directly via GET /api/v1/Order/file/download/{fileId}.',
      ),
    ],
  },
  {
    id: 'webhooks',
    group: DocGroup.Resources,
    title: 'Webhooks',
    lead: 'When order actions are completed in the real world, the system sends status updates to your endpoint — if your group has webhook notifications enabled.',
    blocks: [
      heading('Setup'),
      paragraph(
        'To use webhooks, share your endpoint details with CtrlChain and choose which notifications you want to receive. Currently this functionality is available for shippers.',
      ),
      heading('Notification body'),
      code(
        'webhook notification',
        '{\n  "EventId": "bc4fff3e-cfc3-0000-8d5d-000b12310e5d",\n  "EventType": "PickupEta",\n  "EntityId": "b9b6854f-282f-4e0d-a529-555f23441541",\n  "EntityType": "Order",\n  "MetaData": [\n    { "Key": "OrderReferenceNumber", "Value": "CCA2026-007873.1" },\n    { "Key": "ShipperReference", "Value": "PO-2026-118" }\n  ],\n  "DetailUrl": "https://api.ctrlchain.com/api/v1/order/detail/b9b6854f-282f-4e0d-a529-555f23441541"\n}',
      ),
      paragraph(
        'The DetailUrl matches the order detail endpoint, so your system can pull the full order state the moment a notification arrives. Once PODs are approved, the order detail also exposes a downloadUrl per POD.',
      ),
      heading('Event types'),
      table(
        ['Event', 'Fires when'],
        [
          ['CancellationOrder', 'An order is cancelled.'],
          ['DeliveryConfirmation', 'A delivery is confirmed (cargo unloaded).'],
          ['DeliveryEta', 'The driver confirms the delivery ETA.'],
          ['PickupConfirmation', 'A pickup is confirmed (cargo loaded).'],
          ['PickupEta', 'The driver confirms the pickup ETA.'],
          ['TimeSlotBookingConfirmed', 'A pickup or delivery timeslot is confirmed.'],
          ['UpdateOrderDriverVehicleTrailer', 'Driver, vehicle, or trailer details are updated.'],
          ['UpdatedOrderReferences', 'Order references are updated.'],
          ['PODApproval', 'A POD has been approved after uploading.'],
        ],
      ),
      heading('Listing and retriggering'),
      paragraph(
        'POST /api/v1/Webhook/paginated returns the webhooks that were triggered for your enterprise, including the response status your endpoint returned. If a delivery failed, retry it with POST /api/v1/Webhook/retrigger and the eventId.',
      ),
    ],
  },
];

export const DOC_GROUPS: readonly DocGroup[] = [DocGroup.GettingStarted, DocGroup.Resources];

export const DEFAULT_DOC_ID = 'introduction';
